import { styled } from '@mui/material/styles';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Box, FormLabel } from '@mui/material';
import { ArticleSourceType, ArticleType } from 'pages/knowledge/hooks/useArticlesQuery';
import RenderPostsInEditPlayListModal from 'pages/knowledge/components/RenderPostsInEditPlaylistModal';
import { PostFormData } from 'pages/addPost/hooks/useAddPostForm';
import { useFormContext, useWatch } from 'react-hook-form';
import PostTitle from './PostTitle';
import PostDescription from './PostDescription';
import PostAttachments from './PostAttachments';
import PostProject from './PostProject';
import PostPartner from './PostPartner';
import PostPlaylists from './PostPlaylists';
import PostDivisionsAndSpecSections from './PostDivisionsAndSpecSections';
import useUserData from 'hooks/useUserData';
import { ConfigEntryFieldType, ConfigEntryType } from 'config/config';
import { useConfigQuery } from '@buildwise/libs/configurations';
import HandleOperation from 'components/utils/HandleOperation';
import {
  ArticleFormRelatedRecords,
  ArticleCreationMethods,
  FULL_WIDTH_ARTICLE_FIELDS,
  PostFormSkeleton,
} from '@buildwise/libs/posts';
import RenderFilterFieldByType from '@buildwise/libs/feed/components/RenderFilterFieldByType';
import { FieldWrapper } from '@buildwise/libs/people';
import SuggestionAssignedToField from './SuggestionAssignedToField';
import { TextFieldController } from '@buildwise/libs/ui';
import TargetGroupsField from './TargetGroupsField';
import RegionsField from './RegionsField';
import RetroFormRadioField from '@buildwise/libs/projects/components/retrospectives/RetroFormRadioField';
import RetroFormProjectMilestone from '@buildwise/libs/projects/components/retrospectives/RetroFormProjectMilestone';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import HelpCenterCollectionTitleField from '@buildwise/libs/help-center/components/HelpCenterCollectionTitleField';

type PostDetailsFormPropTypes = {
  article?: ArticleType;
  articleCreationMethod?: ArticleCreationMethods;
  articleType: ArticleSourceType;
  configName: string;
  forward?: boolean;
  isCreatingOrEditingPlaylist?: boolean;
  isEditing: boolean;
  isFullScreen: boolean;
  loadingFiles: boolean;
  selectedTemplateId?: string;
  skeleton?: ReactNode;
  articleFields: ConfigEntryType[];
  openAddArticlesModal?: boolean;
  getFieldWidth?: (options: { field: ConfigEntryType }) => string | undefined;
  onDrop: (acceptedFiles: File[]) => void;
  setOpenAddArticlesModal?: React.Dispatch<React.SetStateAction<boolean>>;
};

const PostDetailsForm: FC<PostDetailsFormPropTypes> = ({
  article,
  articleCreationMethod = ArticleCreationMethods.WRITE_MANUALLY,
  articleType,
  configName,
  forward = false,
  isCreatingOrEditingPlaylist,
  isEditing,
  isFullScreen,
  loadingFiles,
  selectedTemplateId,
  skeleton,
  articleFields,
  openAddArticlesModal,
  getFieldWidth,
  onDrop,
  setOpenAddArticlesModal,
}) => {
  const { companyId } = useUserData();
  const {
    control,
    formState: { isSubmitting },
    watch,
    getValues,
    setValue,
  } = useFormContext<PostFormData>();
  const title = watch('title');
  const [postTitle, setPostTitle] = useState(title);
  const { isLoading: articleFieldsLoading } = useConfigQuery({
    companyId,
    configName,
    shouldFetch: false,
  });
  const targetGroups = useWatch({ control, name: 'targetGroups', defaultValue: [] });
  const region = useWatch({ control, name: 'region', defaultValue: [] });
  const { projectsModule, partnersModule } = useBuildWiseModules();
  const linkPostPlacementField = useMemo(() => {
    let placement = '';
    articleFields.some((field, index) => {
      if (field.entryCode === 'description') {
        placement = 'description';
        return true;
      }

      if (placement !== 'description' && field.entryCode === 'title') {
        placement = 'title';
        return index === articleFields.length - 1;
      }

      if (
        placement !== 'description' &&
        placement !== 'title' &&
        field.entryCode === 'attachments'
      ) {
        placement = 'attachments';
        return index === articleFields.length - 1;
      }
    });

    return placement;
  }, [articleFields]);

  const renderField = (field: ConfigEntryType, index: number) => {
    switch (field.entryCode) {
      case 'title': {
        return (
          <>
            {articleType === ArticleSourceType.LESSON ? (
              <RenderFilterFieldByType autoFocus={index === 0} field={field} />
            ) : articleType === ArticleSourceType.HELP_CENTER && isCreatingOrEditingPlaylist ? (
              <HelpCenterCollectionTitleField field={field} isEditing={isEditing} />
            ) : (
              <PostTitle
                autoFocus={index === 0}
                selectedTemplateId={selectedTemplateId}
                setPostTitle={setPostTitle}
                required={field.referenceObject?.isRequired}
              />
            )}
            {!isCreatingOrEditingPlaylist &&
              linkPostPlacementField === 'title' &&
              articleType !== ArticleSourceType.HELP_CENTER && <ArticleFormRelatedRecords />}
          </>
        );
      }

      case 'lessonTypes': {
        return (
          <>
            <Box mb={0.5}>
              <FieldLabel id={field.entryCode} disabled={isSubmitting}>
                {field.referenceObject?.label || field.entryDescription}
                {field.referenceObject?.isRequired && <Asterisk>*</Asterisk>}
              </FieldLabel>
            </Box>
            <RetroFormRadioField field={field} />
          </>
        );
      }

      case 'facilitatorNotes':
      case 'actionItem': {
        return (
          <>
            <Box mb={0.5}>
              <FieldLabel id={field.entryCode} disabled={isSubmitting}>
                {field.referenceObject?.label || field.entryDescription}
                {field.referenceObject?.isRequired && <Asterisk>*</Asterisk>}
              </FieldLabel>
            </Box>
            <RenderFilterFieldByType autoFocus={index === 0} field={field} />
          </>
        );
      }

      case 'projectMileStones': {
        if (articleType === ArticleSourceType.LESSON) {
          return <RetroFormProjectMilestone field={field} />;
        }
        return null;
      }

      case 'description': {
        return (
          <>
            {(articleType === ArticleSourceType.QUESTION ||
              articleType === ArticleSourceType.SUGGESTION ||
              articleType === ArticleSourceType.LESSON) && (
              <Box mb={0.5} mt={articleType === ArticleSourceType.LESSON ? 0.5 : 0}>
                <FieldLabel id={field.entryCode} disabled={isSubmitting}>
                  {field.referenceObject?.label || field.entryDescription}
                  {field.referenceObject?.isRequired && <Asterisk>*</Asterisk>}
                </FieldLabel>
              </Box>
            )}
            <PostDescription
              autoFocus={index === 0}
              hideToolbar={
                articleType === ArticleSourceType.QUESTION ||
                articleType === ArticleSourceType.SUGGESTION
              }
              isEditing={isEditing}
              placeholder={`${field.referenceObject?.placeHolder || 'Add a description'}${!isCreatingOrEditingPlaylist && field.referenceObject?.isRequired && articleType === ArticleSourceType.POST ? '*' : ''}`}
              {...(articleType === ArticleSourceType.LESSON && { defaultHeight: 75 })}
              articleType={articleType}
            />
            {!isCreatingOrEditingPlaylist &&
              linkPostPlacementField === 'description' &&
              articleType !== ArticleSourceType.HELP_CENTER && <ArticleFormRelatedRecords />}
          </>
        );
      }

      case 'attachments': {
        return (
          <>
            <PostAttachments
              isFullScreen={isFullScreen}
              articleType={articleType}
              article={article}
              onDrop={onDrop}
              loadingFiles={loadingFiles}
              multiple={articleCreationMethod !== ArticleCreationMethods.PARSE_FILE}
            />
            {!isCreatingOrEditingPlaylist &&
              linkPostPlacementField === 'attachments' &&
              articleType !== ArticleSourceType.HELP_CENTER && <ArticleFormRelatedRecords />}
          </>
        );
      }

      case 'topics': {
        if (forward) {
          return null;
        }

        return <RenderFilterFieldByType autoFocus={index === 0} field={field} />;
      }

      case 'projectId': {
        if (!!projectsModule?.isActive && !forward) {
          return <PostProject field={field} readOnly={articleType === ArticleSourceType.LESSON} />;
        }

        return null;
      }

      case 'projectPhases': {
        if (forward) {
          return null;
        }

        return <RenderFilterFieldByType autoFocus={index === 0} field={field} />;
      }

      case 'partnerIds': {
        if (!!partnersModule?.isActive && !forward) {
          return <PostPartner field={field} />;
        }

        return null;
      }

      case 'playListArticleIds': {
        if (isEditing || forward || articleType === ArticleSourceType.HELP_CENTER) {
          return null;
        }

        return <PostPlaylists field={field} />;
      }

      case 'divisions': {
        return (
          <PostDivisionsAndSpecSections
            articleFields={articleFields}
            isFullScreen={isFullScreen}
            postTitle={postTitle}
          />
        );
      }

      // Till the time we separate specSections field as an individual component from PostDivisionsAndSpecSections, return null
      case 'specSections': {
        return null;
      }

      // case 'targetGroups': {
      //   return (
      //     <>
      //       {articleType === ArticleSourceType.SUGGESTION ||
      //       articleType === ArticleSourceType.QUESTION ? (
      //         <TargetGroupsField field={field} articleType={articleType} />
      //       ) : (
      //         <RenderFilterFieldByType autoFocus={index === 0} field={field} />
      //       )}
      //     </>
      //   );
      // }

      // case 'region': {
      //   return (
      //     <>
      //       {articleType === ArticleSourceType.SUGGESTION ||
      //       articleType === ArticleSourceType.QUESTION ? (
      //         <RegionsField field={field} articleType={articleType} />
      //       ) : (
      //         <RenderFilterFieldByType autoFocus={index === 0} field={field} />
      //       )}
      //     </>
      //   );
      // }

      case 'taskOwner': {
        return (
          <SuggestionAssignedToField
            autoFocus={index === 0}
            departments={targetGroups}
            regions={region}
            field={field}
          />
        );
      }

      case 'comment': {
        return (
          <>
            <Box mb={1}>
              <FieldLabel id={field.entryCode} disabled={isSubmitting}>
                {field.referenceObject?.label || field.entryDescription}
                {field.referenceObject?.isRequired && <Asterisk>*</Asterisk>}
              </FieldLabel>
            </Box>
            <TextFieldController<PostFormData>
              name="comment"
              TextFieldProps={{
                id: 'comment',
                variant: 'outlined',
                multiline: true,
                minRows: 5,
                autoFocus: index === 0,
                placeholder: field.referenceObject?.placeHolder,
              }}
            />
          </>
        );
      }

      default: {
        return (
          <RenderFilterFieldByType
            autoFocus={index === 0}
            field={field}
            articleType={articleType}
          />
        );
      }
    }
  };

  // When we reset the title field through resetField method
  // the value of postTitle is not getting updated
  useEffect(() => {
    if (!title) {
      setPostTitle(title);
    }
  }, [title]);

  return (
    <>
      <HandleOperation
        isLoading={articleFieldsLoading}
        skelton={skeleton || <PostFormSkeleton />}
        skeltonCount={1}
      >
        {!linkPostPlacementField && articleType !== ArticleSourceType.HELP_CENTER && (
          <ArticleFormRelatedRecords />
        )}
        {articleFields?.map((field, index) => {
          const _field = renderField(field, index);

          if (_field) {
            return (
              <Box
                key={field.entryCode}
                width={
                  getFieldWidth?.({ field }) ||
                  (FULL_WIDTH_ARTICLE_FIELDS.includes(field.entryCode) ||
                  field.referenceObject?.type === ConfigEntryFieldType.EDITOR
                    ? '100%'
                    : isFullScreen
                      ? '50%'
                      : '70%')
                }
                mt={FULL_WIDTH_ARTICLE_FIELDS.includes(field.entryCode) ? 0 : 1}
              >
                {FULL_WIDTH_ARTICLE_FIELDS.includes(field.entryCode) ||
                field.referenceObject?.type === ConfigEntryFieldType.EDITOR ? (
                  _field
                ) : (
                  <FieldWrapper
                    id={field.entryCode}
                    label={field.referenceObject?.label || field.entryDescription}
                    required={!isCreatingOrEditingPlaylist && field.referenceObject?.isRequired}
                    disabled={isSubmitting}
                  >
                    {_field}
                  </FieldWrapper>
                )}
              </Box>
            );
          }
          return null;
        })}

        {article?.originIndicator && article?.originIndicator === 'PLAYLIST' && (
          <RenderPostsInEditPlayListModal
            playListArticles={article?.playListArticles}
            article={article}
            getValues={getValues}
            setValue={setValue}
            openAddArticlesModal={openAddArticlesModal}
            setOpenAddArticlesModal={setOpenAddArticlesModal}
          />
        )}
      </HandleOperation>
    </>
  );
};

export const FieldLabel = styled(FormLabel)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.custom.tuna,
  marginTop: 40,
  whiteSpace: 'pre-wrap',
}));

export const Asterisk = styled('span')(({ theme }) => ({
  color: theme.palette.custom.paleCarmine,
}));

export default PostDetailsForm;
